/**
 * Utilidades de validación de inputs para prevenir inyección SQL y otros ataques
 */

// Caracteres peligrosos prohibidos en todos los inputs: comilla simple, asterisco, guión
const DANGEROUS_CHARS_PATTERN = /['`*-]|--|\/\*|\*\//g;

// Patrón para detectar caracteres peligrosos
const containsDangerousChars = (value) => {
  return DANGEROUS_CHARS_PATTERN.test(value);
};

/**
 * Sanitiza un input removiendo caracteres peligrosos
 * @param {string} value - El valor a sanitizar
 * @returns {string} - El valor sanitizado
 */
export const sanitizeInput = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  // Remover caracteres peligrosos: ' * - -- /* */
  return value.replace(DANGEROUS_CHARS_PATTERN, '');
};

/**
 * Valida que un input no contenga caracteres peligrosos
 * @param {string} value - El valor a validar
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateInput = (value) => {
  if (!value) {
    return { isValid: true, message: '' };
  }
  
  if (typeof value !== 'string') {
    return { isValid: true, message: '' };
  }
  
  if (containsDangerousChars(value)) {
    return { 
      isValid: false, 
      message: 'Caracteres no permitidos: \' * - -- /* */' 
    };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Valida el nombre de usuario (sin espacios ni caracteres peligrosos)
 * @param {string} username - El nombre de usuario a validar
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateUsername = (username) => {
  if (!username) {
    return { isValid: true, message: '' };
  }
  
  if (typeof username !== 'string') {
    return { isValid: false, message: 'El usuario debe ser texto' };
  }
  
  // Verificar espacios
  if (username.includes(' ')) {
    return { 
      isValid: false, 
      message: 'El nombre de usuario no puede contener espacios' 
    };
  }
  
  // Verificar caracteres peligrosos
  if (containsDangerousChars(username)) {
    return { 
      isValid: false, 
      message: 'Caracteres no permitidos: \' * - -- /* */' 
    };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Valida un campo de búsqueda (sin caracteres peligrosos)
 * @param {string} searchTerm - El término de búsqueda a validar
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateSearchTerm = (searchTerm) => {
  return validateInput(searchTerm);
};

/**
 * Maneja el evento onChange de un input aplicando validación
 * @param {Event} e - El evento de cambio
 * @param {Function} setter - La función setter del estado
 * @param {Function} [errorSetter] - Función opcional para setear mensajes de error
 * @param {boolean} [isUsername=false] - Si es un campo de username
 */
export const handleValidatedChange = (e, setter, errorSetter = null, isUsername = false) => {
  const newValue = e.target.value;
  
  // Sanitizar el valor automáticamente
  const sanitized = sanitizeInput(newValue);
  
  // Si es username, también validar espacios
  if (isUsername && sanitized.includes(' ')) {
    if (errorSetter) {
      errorSetter('El nombre de usuario no puede contener espacios');
    }
    // No actualizar el valor si tiene espacios
    return;
  }
  
  // Validar caracteres peligrosos
  const validation = isUsername ? validateUsername(sanitized) : validateInput(sanitized);
  
  if (!validation.isValid && errorSetter) {
    errorSetter(validation.message);
    return;
  }
  
  if (errorSetter) {
    errorSetter('');
  }
  
  setter(sanitized);
};

/**
 * Formatea un valor numérico con separadores de miles (formato argentino)
 * @param {string} value - El valor a formatear
 * @returns {string} - El valor formateado con separadores de miles
 * Ejemplo: "1234567.89" -> "1.234.567,89"
 */
export const formatCurrencyInput = (value) => {
  if (!value) return '';
  
  // Convertir a string y remover caracteres no numéricos excepto coma y punto
  let cleanValue = value.toString().replace(/[^\d.,]/g, '');
  
  // Si el valor está vacío después de limpiar, retornar vacío
  if (!cleanValue) return '';
  
  // Reemplazar puntos por nada temporalmente (para manejar formato previo)
  cleanValue = cleanValue.replace(/\./g, '');
  
  // Separar parte entera y decimal (usar coma como separador decimal)
  const parts = cleanValue.split(',');
  let integerPart = parts[0] || '';
  const decimalPart = parts[1] || '';
  
  // Si no hay parte entera, retornar vacío
  if (!integerPart) return '';
  
  // Formatear la parte entera con separadores de miles (puntos)
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  // Reconstruir el número
  if (decimalPart) {
    // Limitar a 2 decimales
    const limitedDecimal = decimalPart.substring(0, 2);
    return `${integerPart},${limitedDecimal}`;
  }
  
  // Si hay una coma al final, mantenerla
  if (cleanValue.endsWith(',')) {
    return `${integerPart},`;
  }
  
  return integerPart;
};

/**
 * Formatea un CUIT/CUIL al formato XX-XXXXXXXX-X
 * @param {string} cuit - El CUIT/CUIL a formatear (puede venir con o sin guiones)
 * @returns {string} - El CUIT/CUIL formateado o el valor original si no es válido
 */
export const formatCuit = (cuit) => {
  if (!cuit || typeof cuit !== 'string') return cuit || 'N/A';
  
  // Remover todos los guiones y espacios existentes
  const cleanCuit = cuit.replace(/[-\s]/g, '');
  
  // Verificar que tenga 11 dígitos
  if (cleanCuit.length !== 11 || !/^\d+$/.test(cleanCuit)) {
    // Si no tiene 11 dígitos, devolver el valor original
    return cuit;
  }
  
  // Formatear: XX-XXXXXXXX-X
  return `${cleanCuit.substring(0, 2)}-${cleanCuit.substring(2, 10)}-${cleanCuit.substring(10, 11)}`;
};

/**
 * Convierte fecha de yyyy-mm-dd a dd/mm/yyyy
 * @param {string} fecha - Fecha en formato yyyy-mm-dd o dd/mm/yyyy o dd-mm-yyyy
 * @returns {string} - Fecha en formato dd/mm/yyyy
 */
export const formatDateDDMMYYYY = (fecha) => {
  if (!fecha || fecha === 'N/A' || fecha === '') {
    return '';
  }
  
  try {
    // Si ya está en formato dd/mm/yyyy, devolverla tal cual
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
      return fecha;
    }
    
    // Si está en formato dd-mm-yyyy, convertir / por -
    if (/^\d{2}-\d{2}-\d{4}$/.test(fecha)) {
      return fecha.replace(/-/g, '/');
    }
    
    // Si está en formato yyyy-mm-dd, convertir a dd/mm/yyyy
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      const [anio, mes, dia] = fecha.split('-');
      return `${dia}/${mes}/${anio}`;
    }
    
    return fecha;
  } catch (error) {
    console.error('[formatDateDDMMYYYY] Error formateando fecha:', error);
    return fecha;
  }
};

/**
 * Convierte fecha de dd/mm/yyyy a yyyy-mm-dd
 * @param {string} fecha - Fecha en formato dd/mm/yyyy o dd-mm-yyyy o yyyy-mm-dd
 * @returns {string} - Fecha en formato yyyy-mm-dd
 */
export const formatDateYYYYMMDD = (fecha) => {
  if (!fecha || fecha === 'N/A' || fecha === '') {
    return '';
  }
  
  try {
    // Si ya está en formato yyyy-mm-dd, devolverla tal cual
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      return fecha;
    }
    
    // Si está en formato dd/mm/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
      const [dia, mes, anio] = fecha.split('/');
      return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
    }
    
    // Si está en formato dd-mm-yyyy
    if (/^\d{2}-\d{2}-\d{4}$/.test(fecha)) {
      const [dia, mes, anio] = fecha.split('-');
      return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
    }
    
    return fecha;
  } catch (error) {
    console.error('[formatDateYYYYMMDD] Error convirtiendo fecha:', error);
    return fecha;
  }
};

/**
 * Formatea entrada de fecha mientras el usuario escribe (máscara dd/mm/yyyy)
 * @param {string} value - Valor actual del input
 * @returns {string} - Valor formateado con /
 */
export const formatDateInput = (value) => {
  if (!value) return '';
  
  // Remover todo lo que no sea número
  let cleaned = value.replace(/\D/g, '');
  
  // Limitar a 8 dígitos (ddmmyyyy)
  cleaned = cleaned.substring(0, 8);
  
  // Aplicar formato dd/mm/yyyy
  if (cleaned.length >= 5) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}/${cleaned.substring(4)}`;
  } else if (cleaned.length >= 3) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2)}`;
  } else if (cleaned.length > 0) {
    return cleaned;
  }
  
  return '';
};

/**
 * Valida que una fecha en formato dd/mm/yyyy sea válida
 * @param {string} fecha - Fecha en formato dd/mm/yyyy
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateDateDDMMYYYY = (fecha) => {
  if (!fecha || fecha === '') {
    return { isValid: true, message: '' };
  }
  
  // Verificar formato dd/mm/yyyy
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
    return { isValid: false, message: 'Formato debe ser dd/mm/aaaa' };
  }
  
  const [dia, mes, anio] = fecha.split('/').map(Number);
  
  // Verificar rangos básicos
  if (mes < 1 || mes > 12) {
    return { isValid: false, message: 'Mes inválido' };
  }
  
  if (dia < 1 || dia > 31) {
    return { isValid: false, message: 'Día inválido' };
  }
  
  if (anio < 1900 || anio > 2100) {
    return { isValid: false, message: 'Año inválido' };
  }
  
  // Verificar fecha válida usando objeto Date
  const date = new Date(anio, mes - 1, dia);
  if (date.getDate() !== dia || date.getMonth() !== mes - 1 || date.getFullYear() !== anio) {
    return { isValid: false, message: 'Fecha inválida' };
  }
  
  return { isValid: true, message: '' };
};
